import React, { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { WorkoutSession, Exercise, PlannedExercise, WorkoutSet } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Save, Dumbbell, X, TrendingDown, Smile, MessageSquare } from 'lucide-react';
import { useClientContext } from '@/contexts/ClientContext';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import ExerciseLibrary from './ExerciseLibrary';
import SetTypeBadge from './SetTypeBadge';
import { toast } from 'sonner';

interface EditCompletedSessionDialogProps {
  sessionId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export default function EditCompletedSessionDialog({ sessionId, isOpen, onClose, onSave }: EditCompletedSessionDialogProps) {
  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [trainerNote, setTrainerNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const utils = trpc.useUtils();
  const { activeClient, isViewingClient } = useClientContext();
  const { data: rawSessions = [] } = trpc.sessions.list.useQuery();
  const { data: clientSessions = [] } = trpc.trainerPortal.getClientSessions.useQuery(
    { clientId: activeClient?.clientId! },
    { enabled: isViewingClient && !!activeClient?.clientId }
  );
  const effectiveSessions = isViewingClient ? clientSessions : rawSessions;
  const { data: rawExercises = [] } = trpc.exercises.list.useQuery();

  const upsertNoteMutation = trpc.trainerPortal.upsertTrainerNote.useMutation({
    onSuccess: () => {
      toast.success('Coaching note saved');
      setSavingNote(false);
    },
    onError: () => {
      toast.error('Failed to save note');
      setSavingNote(false);
    },
  });

  const exercises: Exercise[] = rawExercises.map((e) => ({
    id: e.id,
    name: e.name,
    category: e.category,
    isCustom: e.isCustom ?? false,
    trackingMode: (e.trackingMode === 'strength' || e.trackingMode === 'time') ? e.trackingMode : undefined,
    notes: e.notes ?? undefined,
  }));

  const saveSessionMutation = trpc.sessions.save.useMutation({
    onSuccess: () => {
      utils.sessions.list.invalidate();
      utils.sessions.byDate.invalidate();
    },
  });

  useEffect(() => {
    if (sessionId && isOpen) {
      const target = effectiveSessions.find((s) => s.id === sessionId);
      if (target) {
        setSession(JSON.parse(JSON.stringify({
          id: target.id,
          name: target.name,
          date: target.date,
          notes: target.notes ?? undefined,
          isLive: target.isLive ?? false,
          completedAt: target.completedAt ?? undefined,
          plannedExercises: (target.plannedExercises as PlannedExercise[]) ?? [],
          sessionFeeling: (target as any).sessionFeeling ?? undefined,
          recoveryNudge: (target as any).recoveryNudge ?? undefined,
          trainerNotes: (target as any).trainerNotes ?? undefined,
        })));
        setTrainerNote((target as any).trainerNotes ?? '');
      }
    } else {
      setSession(null);
    }
  }, [sessionId, isOpen, effectiveSessions]);

  if (!session || !isOpen) return null;

  const handleAddExercise = (exercise: Exercise) => {
    const newPlannedExercise: PlannedExercise = {
      id: `pe-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      exerciseId: exercise.id,
      sets: [{ id: `set-${Date.now()}-0`, reps: 10, weight: 135, completed: true }],
    };
    setSession((prev) => {
      if (!prev) return null;
      return { ...prev, plannedExercises: [...prev.plannedExercises, newPlannedExercise] };
    });
    setShowLibrary(false);
    toast.success(`Added ${exercise.name} to session`);
  };

  const handleRemoveExercise = (peId: string) => {
    setSession((prev) => {
      if (!prev) return null;
      return { ...prev, plannedExercises: prev.plannedExercises.filter((pe) => pe.id !== peId) };
    });
  };

  const handleAddSet = (peId: string) => {
    setSession((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        plannedExercises: prev.plannedExercises.map((pe) => {
          if (pe.id !== peId) return pe;
          const lastSet = pe.sets[pe.sets.length - 1];
          const newSet: WorkoutSet = {
            id: `set-${Date.now()}-${pe.sets.length}`,
            reps: lastSet ? lastSet.reps : 10,
            weight: lastSet ? lastSet.weight : 135,
            completed: true,
          };
          return { ...pe, sets: [...pe.sets, newSet] };
        }),
      };
    });
  };

  const handleRemoveSet = (peId: string, setId: string) => {
    setSession((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        plannedExercises: prev.plannedExercises.map((pe) => {
          if (pe.id !== peId) return pe;
          if (pe.sets.length <= 1) return pe;
          return { ...pe, sets: pe.sets.filter((s) => s.id !== setId) };
        }),
      };
    });
  };

  const handleSetChange = (peId: string, setId: string, field: 'reps' | 'weight' | 'durationSeconds', value: string) => {
    const numVal = value === '' ? 0 : parseFloat(value);
    setSession((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        plannedExercises: prev.plannedExercises.map((pe) => {
          if (pe.id !== peId) return pe;
          return { ...pe, sets: pe.sets.map((s) => (s.id !== setId ? s : { ...s, [field]: numVal })) };
        }),
      };
    });
  };

  const handleSave = async () => {
    if (!session) return;
    const sanitizedExercises = session.plannedExercises.map((pe) => ({
      ...pe,
      sets: pe.sets.map((s) => ({ ...s, completed: true })),
    }));
    const updatedSession = { ...session, plannedExercises: sanitizedExercises };
    await saveSessionMutation.mutateAsync(updatedSession);
    toast.success('Workout session updated and stats recalculated!');
    onSave();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl bg-card border border-border p-0 overflow-hidden flex flex-col max-h-[90vh]">
        <DialogHeader className="p-4 border-b border-border/50 flex flex-row items-center justify-between bg-secondary/20 space-y-0">
          <div className="flex items-center gap-2">
            <Dumbbell className="h-5 w-5 text-primary" />
            <div>
              <DialogTitle className="font-mono text-sm font-bold uppercase text-foreground">Edit Completed Workout</DialogTitle>
              <div className="flex items-center gap-2">
                <p className="text-[10px] text-muted-foreground font-mono">Logged on {session.date}</p>
                {(session as any).sessionFeeling && (
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-mono text-[9px] font-bold ${
                    (session as any).sessionFeeling === 'crushed_it' ? 'text-green-500 bg-green-500/10 border-green-500/30' :
                    (session as any).sessionFeeling === 'solid' ? 'text-blue-400 bg-blue-400/10 border-blue-400/30' :
                    (session as any).sessionFeeling === 'tough' ? 'text-amber-500 bg-amber-500/10 border-amber-500/30' :
                    'text-destructive bg-destructive/10 border-destructive/30'
                  }`}>
                    <Smile className="h-2.5 w-2.5" />
                    {(session as any).sessionFeeling === 'crushed_it' ? 'CRUSHED IT' :
                     (session as any).sessionFeeling === 'solid' ? 'SOLID' :
                     (session as any).sessionFeeling === 'tough' ? 'TOUGH' : 'COOKED'}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Workout Name</label>
              <Input value={session.name} onChange={(e) => setSession((prev) => (prev ? { ...prev, name: e.target.value } : null))} className="bg-secondary border-border font-mono text-xs" />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Session Notes</label>
                            <Input value={session.notes || ''} onChange={(e) => setSession((prev) => (prev ? { ...prev, notes: e.target.value } : null))} className="bg-secondary border-border text-xs" placeholder="No notes logged" />
            </div>
          </div>
          {/* Recovery nudge from AI feedback loop */}
          {(session as any).recoveryNudge && (
            <div className="p-2.5 bg-primary/5 border border-primary/20 rounded-sm flex items-start gap-2">
              <span className="text-primary text-[10px] mt-0.5">⚡</span>
              <p className="text-[10px] font-mono text-muted-foreground leading-relaxed">{(session as any).recoveryNudge}</p>
            </div>
          )}

          {/* Coaching Notes — trainer writes, client reads */}
          {(isViewingClient || (session as any).trainerNotes) && (
            <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-sm space-y-2">
              <div className="flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-[10px] font-mono text-amber-500 uppercase tracking-wider font-bold">Coaching Note</span>
                {!isViewingClient && <span className="text-[9px] font-mono text-muted-foreground ml-1">(from your trainer)</span>}
              </div>
              {isViewingClient ? (
                <div className="space-y-2">
                  <Textarea
                    value={trainerNote}
                    onChange={(e) => setTrainerNote(e.target.value)}
                    placeholder="Add a coaching note for this session…"
                    className="bg-secondary border-border text-xs font-mono resize-none min-h-[80px]"
                    maxLength={2000}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-mono text-muted-foreground">{trainerNote.length}/2000</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={savingNote}
                      onClick={async () => {
                        if (!activeClient?.clientId || !sessionId) return;
                        setSavingNote(true);
                        await upsertNoteMutation.mutateAsync({
                          sessionId,
                          clientId: activeClient.clientId,
                          note: trainerNote,
                        });
                      }}
                      className="h-6 font-mono text-[10px] border-amber-500/40 text-amber-500 hover:bg-amber-500/5"
                    >
                      {savingNote ? 'SAVING…' : 'SAVE NOTE'}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs font-mono text-muted-foreground leading-relaxed">{(session as any).trainerNotes}</p>
              )}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-border/30 pb-1">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Exercises</span>
              <Button size="sm" variant="outline" onClick={() => setShowLibrary(true)} className="h-6 font-mono text-[10px] border-primary/40 text-primary hover:bg-primary/5">
                <Plus className="h-3 w-3 mr-1" /> ADD EXERCISE
              </Button>
            </div>

            {session.plannedExercises.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border/50 rounded-sm text-xs text-muted-foreground">No exercises in this workout. Add some to log stats.</div>
            ) : (
              <div className="space-y-3">
                {session.plannedExercises.map((pe, peIdx) => {
                  const exercise = exercises.find((e) => e.id === pe.exerciseId);
                  return (
                    <div key={pe.id} className="p-3 bg-secondary/10 border border-border/40 rounded-sm space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-muted-foreground">#{peIdx + 1}</span>
                          <span className="font-bold text-xs text-foreground">{exercise?.name || 'Unknown Lift'}</span>
                          <Badge variant="outline" className="font-mono text-[9px] uppercase px-1.5 py-0">{exercise?.category}</Badge>
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => handleRemoveExercise(pe.id)} className="h-6 w-6 text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>

                      <div className="space-y-1.5">
                        <div className="grid grid-cols-12 gap-2 text-[10px] font-mono text-muted-foreground px-1">
                          <div className="col-span-2">SET</div>
                          {exercise?.trackingMode === 'time' ? (
                            <><div className="col-span-4">DURATION (SECS)</div><div className="col-span-4">WEIGHT (LBS)</div></>
                          ) : (
                            <><div className="col-span-4">WEIGHT (LBS)</div><div className="col-span-4">REPS</div></>
                          )}
                          <div className="col-span-2 text-right">ACTION</div>
                        </div>

                        {pe.sets.map((set, sIdx) => {
                          const s = set as typeof set & {
                            setType?: string;
                            isWarmup?: boolean;
                            rpe?: number;
                            rir?: number;
                            dropSetEntries?: { weight?: number; reps?: number }[];
                          };
                          const isDropSet = s.setType === 'drop_set';
                          return (
                          <div key={set.id} className="space-y-0.5">
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-2 flex items-center gap-1 px-1">
                              <span className="font-mono text-xs text-muted-foreground">{sIdx + 1}</span>
                              {(s.setType || s.isWarmup) && (
                                <SetTypeBadge setType={s.setType as any} isWarmup={s.isWarmup} compact />
                              )}
                            </div>
                            {exercise?.trackingMode === 'time' ? (
                              <>
                                <div className="col-span-4">
                                  <Input type="number" value={set.durationSeconds || ''} onChange={(e) => handleSetChange(pe.id, set.id, 'durationSeconds', e.target.value)} className="bg-background border-border h-7 font-mono text-xs" placeholder="Secs" />
                                </div>
                                <div className="col-span-4">
                                  <Input type="number" step="0.5" value={set.weight || ''} onChange={(e) => handleSetChange(pe.id, set.id, 'weight', e.target.value)} className="bg-background border-border h-7 font-mono text-xs" placeholder="lbs" />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="col-span-4">
                                  <Input type="number" step="0.5" value={set.weight || ''} onChange={(e) => handleSetChange(pe.id, set.id, 'weight', e.target.value)} className="bg-background border-border h-7 font-mono text-xs" placeholder="lbs" />
                                </div>
                                <div className="col-span-4">
                                  <Input type="number" value={set.reps || ''} onChange={(e) => handleSetChange(pe.id, set.id, 'reps', e.target.value)} className="bg-background border-border h-7 font-mono text-xs" placeholder="Reps" />
                                </div>
                              </>
                            )}
                            <div className="col-span-2 text-right">
                              <Button size="icon" variant="ghost" disabled={pe.sets.length <= 1} onClick={() => handleRemoveSet(pe.id, set.id)} className="h-6 w-6 text-muted-foreground hover:text-destructive disabled:opacity-30">
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          {/* RPE / RIR annotation */}
                          {(s.rpe != null || s.rir != null) && (
                            <div className="ml-6 flex items-center gap-2 text-[9px] font-mono text-muted-foreground">
                              {s.rpe != null && (
                                <span className="bg-secondary/60 border border-border/40 px-1.5 py-0.5 rounded-sm">
                                  RPE <span className="text-foreground font-bold">{s.rpe}</span>
                                </span>
                              )}
                              {s.rir != null && (
                                <span className="bg-secondary/60 border border-border/40 px-1.5 py-0.5 rounded-sm">
                                  RIR <span className="text-foreground font-bold">{s.rir}</span>
                                </span>
                              )}
                            </div>
                          )}
                          {/* Drop set entries */}
                          {isDropSet && s.dropSetEntries && s.dropSetEntries.length > 0 && (
                            <div className="ml-6 flex flex-wrap gap-1 mt-0.5">
                              {s.dropSetEntries.map((drop, di) => (
                                <span key={di} className="inline-flex items-center gap-0.5 text-[9px] font-mono text-amber-500/80 bg-amber-500/5 border border-amber-500/20 px-1.5 py-0.5 rounded-sm">
                                  <TrendingDown className="h-2.5 w-2.5" />
                                  ↓{di + 1}: {drop.weight ?? '?'} lbs × {drop.reps ?? '?'}
                                </span>
                              ))}
                            </div>
                          )}
                          </div>
                          );
                        })}
                      </div>

                      <Button size="sm" variant="ghost" onClick={() => handleAddSet(pe.id)} className="w-full h-6 border border-dashed border-border/60 hover:border-primary/50 text-[10px] font-mono text-muted-foreground hover:text-primary mt-1">
                        <Plus className="h-3 w-3 mr-1" /> ADD SET
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="p-3 border-t border-border/50 flex justify-between bg-secondary/20 flex-row sm:justify-between">
          <Button variant="outline" size="sm" onClick={onClose} className="font-mono text-xs">CANCEL</Button>
          <Button onClick={handleSave} disabled={saveSessionMutation.isPending} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs">
            <Save className="h-3.5 w-3.5 mr-1" /> SAVE CHANGES
          </Button>
        </DialogFooter>

        {showLibrary && (
          <div className="absolute inset-0 bg-background/95 z-50 p-4 overflow-y-auto flex flex-col">
            <div className="flex justify-between items-center mb-3 pb-2 border-b border-border/40">
              <span className="text-xs font-mono text-primary font-bold uppercase">Exercise Vault</span>
              <Button size="sm" variant="ghost" onClick={() => setShowLibrary(false)} className="h-7 font-mono text-[10px]">
                <X className="h-3 w-3 mr-1" /> CLOSE
              </Button>
            </div>
            <div className="flex-1">
              <ExerciseLibrary onSelectExercise={handleAddExercise} showSelectButton={true} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
